import { Capacitor } from '@capacitor/core'
import { t } from '@lingui/macro'
import { isNil } from 'ramda'
import { useNavigate, useParams } from 'react-router-dom'
import styled, { useTheme } from 'styled-components'

import { Card } from '@/@bothrs-ui/web/components/card/Card'
import { Flex, FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { H2, H3, H4 } from '@/@bothrs-ui/web/components/typography/Heading'
import { breakpoints } from '@/config/layout'
import { useHideNativeNavigation, useHideWebHeader } from '@/layout/providers/LayoutProvider'
import { ContentItemDetailLayout } from '@/modules/content/components/ContentItemDetailLayout'
import { RichText } from '@/modules/content/components/RichText'
import { serializeHTML } from '@/modules/content/components/RichText/serialize'
import { useOrders } from '@/modules/orders/hooks/useOrders'
import { getCurrencySign, mapPriceToCurrency } from '@/modules/payment/utils/payment.helpers'
import { useToast } from '@/modules/shared/hooks/useToast'
import { openUrl } from '@/utils/external-resources'
import { useCareProgramById } from '../../hooks/useCarePrograms'

import type { CreateOrderRequest } from '@/modules/orders/types/order.type'

const CareProgramDetail = () => {
  // Hooks
  // ------------------------------------------------------------------------- /

  const theme = useTheme()
  const { id } = useParams()
  const navigate = useNavigate()

  const { isLoading, data: careProgram } = useCareProgramById(id)
  const { createOrder } = useOrders()

  const { showErrorToast } = useToast()

  // Hide navigation on native devices
  useHideWebHeader()
  useHideNativeNavigation()

  // Template variables
  // ------------------------------------------------------------------------- /

  // For now, no support for multiple pricings
  const price = careProgram?.price?.[0]

  const priceDisplay = price && `${getCurrencySign(price.price_currency)}${mapPriceToCurrency(price.price_amount)}`

  // Interaction methods
  // ------------------------------------------------------------------------- /

  /**
   * This function is used to buy a care program.
   * It will create a payment link and open it in the browser.
   * If the payment link is not created, it will show an error toast.
   */
  const handleBuy = async () => {
    if (!careProgram?.title || !price?.price_amount || !careProgram?.id) {
      showErrorToast({
        message: t({
          id: 'careProgramDetail.buyError.missingData',
          message: 'Het is momenteel niet mogelijk om deze dienst te kopen.',
        }),
      })
      return
    }

    const order: CreateOrderRequest = {
      display: careProgram?.title,
      channel: Capacitor.isNativePlatform() ? 'APP' : 'WEB',
      orderLines: [
        {
          display: careProgram?.title,
          price: price?.price_amount,
          externalId: careProgram?.id,
        },
      ],
    }

    try {
      const orderResponse = await createOrder(order)
      if (orderResponse?.data?.orderPaymentLink?.pomDocumentId) {
        navigate(`/care/payment-status/${orderResponse.data.orderPaymentLink?.pomDocumentId}`)
      }
      setTimeout(() => {
        if (orderResponse?.data?.orderPaymentLink?.paylink) {
          openUrl(orderResponse.data.orderPaymentLink.paylink, {
            target: '_self',
          })
        }
      }, 300)
    } catch (error) {
      console.error(error)
      showErrorToast({
        message: t({
          id: 'careProgramDetail.buyError',
          message:
            'Er is een fout opgetreden bij het aankopen van de dienst. Gelieve contact op te nemen bij onze klantenservice.',
        }),
      })
    }
  }

  const handleGoBack = () => {
    navigate('/care')
  }

  // Render
  // ------------------------------------------------------------------------- /
  return (
    <ContentItemDetailLayout
      loading={isLoading}
      bannerUrl={careProgram?.featuredImage?.url}
      bannerContent={
        <StBannerContent>
          <H2>{careProgram?.title}</H2>
          <H4 style={{ fontWeight: 500 }}>{careProgram?.description && serializeHTML(careProgram?.description)}</H4>
        </StBannerContent>
      }
      footer={
        <StButtonRow gap={theme.UI.Spacing.Space3}>
          {price && !isNil(price.price_amount) && price.price_amount > 0 ? (
            <Button variant='Primary' onClick={handleBuy}>
              {t({
                id: 'careProgramDetail.buyButton',
                message: '{price} betalen',
                values: {
                  price: priceDisplay,
                },
              })}
            </Button>
          ) : null}

          <Button variant='Secondary' onClick={() => navigate('/care/enroll-form?programId=' + id)}>
            {price
              ? t({
                  id: 'careProgramDetail.askForMoreInfo',
                  message: 'Vraag meer info',
                })
              : t({
                  id: 'careProgramDetail.enrollButton.disabled',
                  message: 'Ik heb interesse',
                })}
          </Button>
        </StButtonRow>
      }
      onGoBack={handleGoBack}
    >
      <StContentContainer>
        {careProgram?.contentBlocks ? <RichText contentBlocks={careProgram.contentBlocks} /> : null}

        {price ? (
          <>
            <H3>
              {t({
                id: 'care.detail.cost.title',
                comment: 'Cost section title',
                message: 'Hoeveel kost dit?',
              })}
            </H3>
            <Card>
              <Flex justifyContent={'space-between'}>
                <H4>{price.price_title}</H4>
                <H4>{priceDisplay}</H4>
              </Flex>
              <RichText contentBlocks={[]} />
              {price.price_description && (
                <StPriceDescription>{serializeHTML(price.price_description)}</StPriceDescription>
              )}
            </Card>
          </>
        ) : null}
      </StContentContainer>
    </ContentItemDetailLayout>
  )
}

const StBannerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space3};

  padding: ${({ theme }) => theme.UI.SpacingPx.Space6};

  * {
    color: ${({ theme }) => theme.Colors.Neutrals.Lightest};
    margin: 0;
  }

  p {
    font-weight: 500;
  }
`

const StContentContainer = styled.div`
  flex: 1;
  overflow-y: auto;

  padding-top: ${({ theme }) => theme.UI.SpacingPx.Space4};
  padding-bottom: ${({ theme }) => theme.UI.SpacingPx.Space8};
`

const StPriceDescription = styled.div`
  ul {
    padding-left: 1.5em;
    margin-bottom: 1em;
    list-style-type: square;
  }

  ol {
    padding-left: 1.5em;
    margin-bottom: 1em;
    list-style-type: decimal;
  }

  li {
    margin-bottom: 0.5em;
  }
`

const StButtonRow = styled(FlexColumn)`
  @media ${breakpoints.fromM} {
    flex-direction: row-reverse !important;
    & > button {
      flex: 1;
    }
  }
`

export { CareProgramDetail }
